<script setup>
import { ref } from 'vue'
import { useWebSiteStore } from '@/stores/website.store'
import { useNotify } from '@/composables/notify'
import { useRouter } from 'vue-router'
import i18n from '@/plugins/i18n'

const router = useRouter()

const username = ref('')
const password = ref('')
const isPwd = ref(true)
const loading = ref(false)
const missingAccountValidation = ref(false)

const websiteStore = useWebSiteStore()
const t = i18n.global.t
const {notifyError, notifySuccess} = useNotify()

const emailRule = (val) =>
  !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) || t('form.field.invalidEmail')

const required = (val) => !!val || t('form.field.required')

const loginAsync = async() => {
  ('Attempting login with:', username.value)
  try {
    loading.value = true
    await websiteStore.loginAsync(username.value, password.value)
  } catch (error) {
    const res_error = error.response?.data || { message: 'error.loginFailed' }
    notifyError(t(res_error.message))
    if (res_error.code === 'account_not_validated') {
      missingAccountValidation.value = true
    }
  } finally {
    loading.value = false
  }
}

const sendValidationEmailAsync = async() => {
  try {
    loading.value = true
    const res = await websiteStore.sendValidationEmailAsync(username.value)
    notifySuccess(t(res.data.message))
  } catch (error) {
    const res_error = error.response?.data || { message: 'error.resendValidationEmailFailed' }
    notifyError(t(res_error.message))
  } finally {
    loading.value = false
  }
}

const onForgotPasswordAsync = async() => {
  router.push({ name: 'request-reset-password' })
}

</script>

<template>
  <q-layout view="lHh Lpr lFf">
    <q-page-container>
      <q-page class="flex flex-center bg-gradient">
        <div class="column items-center">
          <div class="column items-center q-gutter-md">
            <div class="text-h5 q-mb-md">
              {{ $t('auth.welcome') }}
            </div>
            <q-avatar
              size="6rem"
              class="bg-white"
            >
              <img
                src="@/assets/extended-logo.svg"
                alt="Logo"
                style="max-width:80%;max-height:80%;object-fit:contain;"
              >
            </q-avatar>
          </div>

          <div class="q-mt-xl column login-card">
            <q-banner
              v-if="missingAccountValidation"
              dense
              rounded
              inline-actions
              class="bg-warning text-white"
            >
              <div class="text-body2">
                {{ $t('auth.accountNotValidated') }}
                <a
                  href="#"
                  @click.prevent="sendValidationEmailAsync"
                >{{ $t('auth.resendValidationEmail') }}</a>
              </div>
            </q-banner>
            <q-form
              ref="loginForm"
              class="column"
              @submit.prevent="loginAsync"
            >
              <q-input
                v-model="username"
                class="q-mt-md"
                :placeholder="$t('auth.email')"
                :rules="[required, emailRule]"
              >
                <template #prepend>
                  <q-icon name="mdi-email" />
                </template>
              </q-input>

              <q-input
                v-model="password"
                outlined
                class="q-mt-md"
                :type="isPwd ? 'password' : 'text'"
                :placeholder="$t('auth.password')"
                lazy-rules
                :rules="[required]"
              >
                <template #prepend>
                  <q-icon name="mdi-lock" />
                </template>
                <template #append>
                  <q-icon
                    :name="isPwd ? 'mdi-eye-off' : 'mdi-eye'"
                    class="cursor-pointer"
                    @click="isPwd = !isPwd"
                  />
                </template>
              </q-input>

              <div class="q-mt-lg">
                <q-btn
                  color="primary"
                  class="full-width q-pa-sm"
                  :label="$t('auth.login')"
                  :loading="loading"
                  type="submit"
                />
              </div>
            </q-form>

            <div class="login-actions column items-center q-mt-md">
              <div class="no-account row items-center q-mt-xs">
                <span class="text-white">{{ $t('auth.noAccount') }}</span>
                <router-link
                  to="/register"
                  class="signup-link q-ml-sm"
                >
                  {{ $t('auth.signUp') }}
                </router-link>
              </div>

              <a
                href="#"
                class="link"
                @click.prevent="onForgotPasswordAsync"
              >
                {{ $t('auth.forgotPassword') }}
              </a>
            </div>
          </div>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/auth-forms.scss';
</style>
